import * as Flashphoner from '@flashphoner/websdk';
import * as RoomApi from '@flashphoner/websdk/src/room-module.js';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from 'components/modules/Loading';
import { addChat, createRoom, expireRoom, getChats, inviteUser, updateRoom } from 'services/api-service/center';
import {
  buildCommand,
  ControlCommand,
  ControlValue,
  encodeMessage,
  STATE_REPORT,
} from 'services/control-protocol';
import { ActionCtor } from 'store/modules/main';
import { IStoreState } from '../../store/modules';
import * as FPUtils from '../modules/fp-utils';
import { HiddenAudioPlayer, isViewerName } from '../modules/hidden-audio';
import { ICE_MEDIA_OPTIONS } from '../modules/ice-config';
import CenterHeader from './CenterHeader';
import CenterList from './list/CenterList';
import CenterMap from './map/CenterMap';
import CenterMessage from './message/CenterMessage';
import ModalAlert from './ModalAlert';
import CenterStream from './stream/CenterStream';
import CenterRescue from './user/CenterRescue';
import CenterVictim from './user/CenterVictim';

const SESSION_STATUS = Flashphoner.constants.SESSION_STATUS;
const STREAM_STATUS = Flashphoner.constants.STREAM_STATUS;
const ROOM_EVENT = RoomApi.events;
const Browser = Flashphoner.Browser;
const PRELOADER_URL = process.env.PUBLIC_URL + '/media/preloader.mp4';
const maxParticipants = 40;

interface ConferenceState {
  apiFailed: string;
  session: any;
  sessionStatus: string;
  sessionFailedInfo: string;
  publishStream: null;
  publishStatus: string;
  serverUrl: string;
  serverUrlDisabled: boolean;
  login: string;
  record: boolean;
  recordDisplay: boolean;
  room: any;
  participants: any[];
  chat: any[];
  message: string;
}

const CenterTemplate = () => {
  const dispatch = useDispatch();
  const selectedRoom = useSelector((store: IStoreState) => store.MainReducer.selectedRoom);
  const receivedCommand = useSelector((store: IStoreState) => store.MainReducer.receivedCommand);
  const receivedMessage = useSelector((store: IStoreState) => store.MainReducer.receivedMessage);
  const userInfo = useSelector((store: IStoreState) => store.UserReducer.userInfo);
  const [loading, setLoading] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [date, setDate] = useState<string>();
  const [roomId, setRoomId] = useState<string | undefined>(undefined);
  const [participant, setParticipant] = useState<string | undefined>();
  const [message, setMessage] = useState<any | undefined>();
  const [conference, setConference] = useState<ConferenceState>({
    apiFailed: '',
    session: null,
    sessionStatus: '',
    sessionFailedInfo: '',
    publishStream: null,
    publishStatus: '',
    // serverUrl: 'wss://demo.flashphoner.com:8443',
    serverUrl: 'wss://smartsar.kr/1/ws',
    serverUrlDisabled: false,
    login: 'center__',
    record: false,
    recordDisplay: false,
    room: null,
    participants: [],
    chat: [],
    message: '',
  });
  const [refreshMediaTimestamp, setRefreshMediaTimestamp] = useState<number | null>(null);
  const viewerAudioRef = useRef(new HiddenAudioPlayer());

  const toastMessage = (message: string) => <div style={{ fontSize: '1.1rem' }}>{message}</div>;

  const playViewerAudio = (roomParticipant) => {
    if (isViewerName(roomParticipant.name())) {
      viewerAudioRef.current.play(roomParticipant);
    }
  };

  const onJoined = (room) => {
    // console.log('onJoined ', room.getParticipants());
    setConference((prevState) => ({
      ...prevState,
      room: room,
    }));
  };

  const onLeft = () => {
    // console.log('onLeft ');
    viewerAudioRef.current.stopAll();
    setConference((prevState) => ({
      ...prevState,
      room: null,
      participants: [],
      chat: [],
      message: '',
    }));
  };

  const onDisconnected = () => {
    setConference((prevState) => ({
      ...prevState,
      session: null,
    }));
    const room = conference.room;
    if (room !== null) {
      room.leave().then(
        () => {
          onLeft();
        },
        () => {
          onLeft();
        }
      );
    }
  };

  const createConnection = (roomId: string) => {
    const session = conference.session;
    const url = conference.serverUrl;
    const username = conference.login + userInfo.userIdx;

    if (session && session.status() === SESSION_STATUS.ESTABLISHED) {
      if (session.getServerUrl() !== url || session.username() !== username) {
        session.on(SESSION_STATUS.DISCONNECTED, () => {
          setConference((prevState) => ({
            ...prevState,
            session: null,
          }));
          createConnection(roomId);
        });
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        session.on(SESSION_STATUS.FAILED, () => {});
        session.disconnect();
      } else {
        joinRoom(roomId, session);
      }
    } else {
      // console.log('Create new RoomApi session with url ' + url + ', login ' + username);
      setConference((prevState) => ({
        ...prevState,
        joinButtonDisabled: true,
        serverUrlDisabled: true,
      }));
      const options = {
        urlServer: url,
        username: username,
        mediaOptions: ICE_MEDIA_OPTIONS,
      };
      RoomApi.connect(options)
        .on(SESSION_STATUS.ESTABLISHED, (session: any) => {
          setConference((prevState) => ({
            ...prevState,
            session: session,
            sessionStatus: SESSION_STATUS.ESTABLISHED,
          }));
          joinRoom(roomId, session);
        })
        .on(SESSION_STATUS.DISCONNECTED, () => {
          setConference((prevState) => ({
            ...prevState,
            sessionStatus: SESSION_STATUS.DISCONNECTED,
          }));
          onDisconnected();
        })
        .on(SESSION_STATUS.FAILED, () => {
          setConference((prevState) => ({
            ...prevState,
            sessionStatus: SESSION_STATUS.FAILED,
          }));
          onDisconnected();
        });
    }
  };

  const joinRoom = (roomId: string, session) => {
    const roomName = roomId;
    const record = conference.record;

    // console.log('Join the room ' + roomName + ', session ' + session);
    session
      .join({ name: roomName, record: record })
      .on(ROOM_EVENT.STATE, (room) => {
        const roomParticipants = room.getParticipants();
        const participantsNumber = roomParticipants.length;
        // console.log('Current number of participants in the room: ' + participantsNumber);
        const memberCount = roomParticipants.filter((p) => !p.name().startsWith('viewer__')).length;
        if (memberCount >= maxParticipants) {
          console.warn('Current room is full');
          setConference((prevState) => ({
            ...prevState,
            sessionFailedInfo: 'Current room is full',
          }));
          room.leave().then(
            () => {
              onLeft();
            },
            () => {
              onLeft();
            }
          );
          return false;
        }
        if (participantsNumber > 0) {
          let chatState = 'participants: ';
          for (let i = 0; i < participantsNumber; i++) {
            dispatch(ActionCtor.addParticipant(roomParticipants[i]));
            installParticipant(roomParticipants[i]);
            chatState += roomParticipants[i].name();
            if (i < participantsNumber - 1) {
              chatState += ',';
            }
            if (roomParticipants[i].getStreams().length > 0) {
              dispatch(ActionCtor.addPublisher(roomParticipants[i]));
              playViewerAudio(roomParticipants[i]);
            }
          }
          // addMessage('chat', chatState);
        } else {
          // addMessage('chat', ' room is empty');
        }
        if (Browser.isSafariWebRTC()) {
          Flashphoner.playFirstVideo(document.getElementById('localDisplay'), true, PRELOADER_URL).then(() => {
            publishLocalMedia(room);
            onJoined(room);
          });
          return;
        }
        publishLocalMedia(room);
        onJoined(room);
      })
      .on(ROOM_EVENT.JOINED, (participant) => {
        // console.log('JOINED: ' + participant);
        dispatch(ActionCtor.addParticipant(participant));
        installParticipant(participant);
        dispatch(ActionCtor.getUsers(roomId));
        dispatch(ActionCtor.getRoomInfos(date));
      })
      .on(ROOM_EVENT.LEFT, (participant) => {
        // console.log('LEFT: ' + participant.name());
        dispatch(ActionCtor.removePublisher(participant));
        dispatch(ActionCtor.removeParticipant(participant));
        viewerAudioRef.current.stop(participant.name());
        // removeParticipant(participant);
        dispatch(ActionCtor.getUsers(roomId));
        dispatch(ActionCtor.getRoomInfos(date));
      })
      .on(ROOM_EVENT.PUBLISHED, (participant) => {
        // console.log('PUBLISHED: ' + participant);
        dispatch(ActionCtor.addPublisher(participant));
        playViewerAudio(participant);
      })
      .on(ROOM_EVENT.FAILED, (room, info) => {
        // console.log('FAILED: ' + info);
        session.disconnect();
        setConference((prevState) => ({
          ...prevState,
          sessionFailedInfo: info,
        }));
      })
      .on(ROOM_EVENT.MESSAGE, (message) => {
        if (message.from && message.text) {
          dispatch(ActionCtor.setCommand(message));
        }
      });
  };

  const parseCommand = (message: any) => {
    try {
      handleCommand(message);
    } catch (error) {
      console.log('parseCommand Error ', error, message);
    }
  };

  const handleCommand = (message: any) => {
    const received = JSON.parse(decodeURIComponent(message.text));
    // console.log('MESSAGE: ' + message.from.name(), received.type);
    if (received.type === 'Control') {
      if (received.cmd === STATE_REPORT && received.state) {
        dispatch(
          ActionCtor.setDeviceReport({
            phone: received.phone || message.from.name(),
            caps: received.caps,
            state: received.state,
          })
        );
      }
    } else if (received.type === 'Information') {
      // Accident Information
    } else if (received.type === 'Chat') {
      // Update Chat Message
      dispatch(ActionCtor.addMessage(JSON.parse(decodeURIComponent(message.text))));
    } else if (received.type === 'Update') {
      // Update video lists
      setRefreshMediaTimestamp(Date.now());
    } else {
      // Update location
      dispatch(ActionCtor.updateLocations([message.from.name(), JSON.parse(decodeURIComponent(message.text))]));
      setParticipant(message.from.name());
      setMessage(message);
    }
  };

  useEffect(() => {
    if (receivedCommand != undefined) {
      parseCommand(receivedCommand);
    }
  }, [receivedCommand]);

  const addMessage = (message) => {
    // console.log('addMessage ' + message);
    setConference((prevState) => ({
      ...prevState,
      chat: [...prevState.chat, message],
    }));
  };

  const installParticipant = (roomParticipant) => {
    const name = roomParticipant.name();

    if (conference.participants.length >= maxParticipants) {
      console.warn('More than ' + maxParticipants + ' participants, ignore participant ' + name);
    } else {
      // console.log('Add participant ' + name);
      // const participants = [
      //   ...conference.participants,
      //   {
      //     name: name,
      //     roomParticipant: roomParticipant,
      //     stream: null,
      //   },
      // ];
      // setConference((prevState) => ({
      //   ...prevState,
      //   participants: participants,
      // }));
      setConference((prevState) => ({
        ...prevState,
        participants: [
          ...conference.participants,
          {
            name: name,
            roomParticipant: roomParticipant,
            stream: null,
          },
        ],
      }));
    }
  };

  const onMediaPublished = (stream) => {
    setConference((prevState) => ({
      ...prevState,
      publishStream: stream,
      publishButtonText: 'Stop',
      publishButtonDisabled: false,
      muteAudioButtonText: 'Mute A',
      muteAudioButtonDisabled: false,
      muteVideoButtonText: 'Mute V',
      muteVideoButtonDisabled: false,
    }));
  };

  const onMediaStopped = () => {
    const session = conference.session;
    let disablePublish = false;

    if (!session || session.getRooms().length === 0) {
      disablePublish = true;
    }
    setConference((prevState) => ({
      ...prevState,
      publishStream: null,
      publishButtonText: 'Publish',
      publishButtonDisabled: disablePublish,
      muteAudioButtonText: 'Mute A',
      muteAudioButtonDisabled: true,
      muteVideoButtonText: 'Mute V',
      muteVideoButtonDisabled: true,
    }));
  };

  const publishLocalMedia = (room) => {
    const constraints = {
      audio: true,
      video: false,
    };
    const display = document.getElementById('localDisplay');

    setConference((prevState) => ({
      ...prevState,
      publishButtonDisabled: true,
    }));
    room
      .publish({
        display: display,
        constraints: constraints,
        record: false,
        receiveVideo: false,
        receiveAudio: false,
      })
      .on(STREAM_STATUS.FAILED, (stream) => {
        console.warn('Local stream failed!');
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.FAILED,
          publishStatusClass: 'text-danger',
        }));
        onMediaStopped();
      })
      .on(STREAM_STATUS.PUBLISHING, (stream) => {
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.PUBLISHING,
          publishStatusClass: 'text-success',
        }));
        onMediaPublished(stream);
      })
      .on(STREAM_STATUS.UNPUBLISHED, (stream) => {
        setConference((prevState) => ({
          ...prevState,
          publishStatus: STREAM_STATUS.UNPUBLISHED,
          publishStatusClass: 'text-success',
        }));
        onMediaStopped();
      });
  };

  const removeParticipant = (roomParticipant) => {
    const participants: any = conference.participants;
    const name = roomParticipant.name();
    for (let i = 0; i < participants.length; i++) {
      if (participants[i].roomParticipant.name() === name) {
        stopParticipantStream(participants[i].stream);
        // setState(
        //   update(conference, {
        //     participants: {
        //       $splice: [[i, 1]],
        //     },
        //   })
        // );
        break;
      }
    }
  };

  const stopParticipantStream = (stream) => {
    if (stream && FPUtils.isPlaying(stream.status())) {
      stream.stop();
    }
  };

  const sendInviteMessage = async (phone: string, type: string) => {
    // console.log('sendInviteMessage Click ', phone, ' / ', type);
    const regExp = /\(.*?\)/g;
    const name = Array.from(phone.matchAll(regExp), (match) => `${match[0]}`);
    if (roomId === undefined) {
      toast(toastMessage('사건 등록 후 호출바랍니다.'), { type: 'error' });
    } else if (phone === '') {
      toast(toastMessage('전화번호 입력 후 호출바랍니다.'), { type: 'error' });
    } else {
      try {
        const res: any = await inviteUser(
          roomId,
          phone.toString().replace(regExp, ''),
          name.toString().replace(/[-/\\^$*+?.()|[\]{}]/g, ''),
          type
        );
        if (res.result === 'fail') {
          throw res;
        }
        // Update users & lists
        dispatch(ActionCtor.getUsers(roomId));
        dispatch(ActionCtor.getRoomInfos(date));
      } catch (error) {
        console.log('sendInviteMessage Error ', error);
      }
    }
  };

  const sendControl = (number: string, type: string) => {
    // console.log('sendControl ', number, type, conference);
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      // console.log('sendControl ', participants);
      for (let i = 0; i < participants.length; i++) {
        // console.log('sendControl Compare', participants.name(), number);
        participants[i].sendMessage(encodeURIComponent(JSON.stringify(location)));
      }
    }
  };

  const onRegisterCase = async (
    date: string,
    time: string,
    lat: string,
    lng: string,
    loc: string,
    dtl: string,
    rslt: string | undefined,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    mdn: string,
    alimType: string
  ): Promise<boolean> => {
    try {
      if (roomId === undefined) {
        const response: any = await createRoom(1, userInfo.userIdx);
        if (response.result === 'fail') {
          toast(toastMessage(response.error_msg || '방 생성 실패'), { type: 'error' });
          return false;
        }
        const resId = response.roomId;
        await updateRoom(
          resId,
          lat,
          lng,
          date,
          time,
          loc,
          dtl,
          undefined,
          dLoc,
          wCond,
          accType,
          dType,
          dRoute,
          mdn,
          alimType
        );
        selectRoom(resId, false);
        toast(toastMessage('사건 정보가 등록되었습니다.'), { type: 'success' });
      } else {
        const response: any = await updateRoom(
          roomId,
          lat,
          lng,
          date,
          time,
          loc,
          dtl,
          rslt,
          dLoc,
          wCond,
          accType,
          dType,
          dRoute,
          mdn,
          alimType
        );
        if (response.result === 'fail') {
          toast(toastMessage(response.error_msg || '수정 실패'), { type: 'error' });
          return false;
        }
        toast(toastMessage('사건 정보가 수정되었습니다.'), { type: 'success' });
      }

      const offset = new Date().getTimezoneOffset() * 60000;
      const today = new Date(Date.now() - offset).toISOString().slice(0, 10).replace(/-/g, '');
      dispatch(ActionCtor.getRoomInfos(today));

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const selectRoom = async (roomId: string, isExpire: boolean) => {
    // console.log('selectRoom ', roomId, isExpire);
    // Start Loading
    setLoading(true);
    setTimeout(function () {
      setLoading(false);
    }, 1500);
    onDisconnected();
    dispatch(ActionCtor.clearPublisher());
    dispatch(ActionCtor.selectRoom(roomId));
    if (!isExpire) {
      createConnection(roomId);
    }
    setRoomId(roomId);
    dispatch(ActionCtor.getLocations(roomId));
    dispatch(ActionCtor.getRoom(roomId));
    dispatch(ActionCtor.getUsers(roomId));
    try {
      const response: any = await getChats(roomId);
      setConference((prevState) => ({
        ...prevState,
        chat: [...conference.chat, ...response.data],
      }));
    } catch (error) {
      // console.log('getChats Error ', error);
    }
  };

  const disconnect = async () => {
    if (selectedRoom === undefined) {
      toast(toastMessage('연결 후 종료바랍니다.'), { type: 'error' });
    }
    try {
      const response: any = await expireRoom(selectedRoom, userInfo.userIdx);
      // console.log('disconnect ', response);
      // 1. Send Expire Event to participants
      if (conference.session && conference.room) {
        const participants = conference.room.getParticipants();
        const ExpireItems: any = {
          type: 'Expire',
        };
        for (let i = 0; i < participants.length; i++) {
          // console.log('sendControl Message', participants);
          participants[i].sendMessage(encodeURIComponent(JSON.stringify(ExpireItems)));
        }
      }
      // 2. Clear RoomId & RoomInfos
      onInitialize();
      // Clear Map
    } catch (error) {
      console.log('disconnect Error ', error);
    }
  };

  const onInitialize = async () => {
    // console.log('onInitialize ', date);
    onDisconnected();
    setRoomId(undefined);
    dispatch(ActionCtor.selectRoom(undefined));
    dispatch(ActionCtor.getRoomInfos(date));
    dispatch(ActionCtor.clearUsers());
    dispatch(ActionCtor.clearPublisher());
    dispatch(ActionCtor.clearRoom());
    dispatch(ActionCtor.clearLocations());
    dispatch(ActionCtor.clearPassenger());
    setConference((prevState) => ({
      ...prevState,
      chat: [],
    }));
  };

  const onSetDate = (date: string) => {
    setDate(date);
  };

  const onMute = (muted: boolean) => {
    const stream: any = conference.publishStream;
    // console.log('onMute ', muted);
    if (stream) {
      if (muted) {
        stream.muteAudio();
      } else {
        stream.unmuteAudio();
      }
    }
  };

  const onChat = () => {
    setOpenChat(!openChat);
  };

  const onSendChat = async (msg: string) => {
    // console.log('onSendChat ', msg);
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      const ChatItems: any = {
        type: 'Chat',
        roomId: selectedRoom,
        chatId: userInfo.userIdx,
        chatNm: userInfo.userName,
        msg,
        chatType: 3,
      };
      for (let i = 0; i < participants.length; i++) {
        // console.log('onSendChat Message', participants);
        participants[i].sendMessage(encodeURIComponent(JSON.stringify(ChatItems)));
      }
      try {
        await addChat(selectedRoom, userInfo.userIdx, userInfo.userName, msg, 3);
      } catch (error) {
        console.log('addChat Error ', error);
      }
      // Upate Chat
      addMessage(ChatItems);
    }
  };

  const onControl = async (cmd: ControlCommand, value: ControlValue, name: string) => {
    if (conference.session && conference.room) {
      const participants = conference.room.getParticipants();
      const targetParticipant = participants.find((participant) => participant.name() === name);
      if (targetParticipant) {
        targetParticipant.sendMessage(encodeMessage(buildCommand(cmd, value, selectedRoom)));
      }
    }
  };

  useEffect(() => {
    if (receivedMessage !== undefined) {
      // console.log('receivedMessage ', receivedMessage);
      addMessage(receivedMessage);
      // Scroll ??
    }
  }, [receivedMessage]);

  useEffect(() => {
    try {
      Flashphoner.init({});
    } catch (e) {
      console.log(e);
    }
  }, []);

  const [warningInfo, setWarningInfo] = useState<
    | {
        isOpen: boolean;
        raiIdx: string;
        shipName: string;
        name: string;
        phoneNumber: string;
        lastReceivedTime: string;
        lastLocLat: string;
        lastLocLon: string;
        alimType: string;
      }
    | undefined
  >(undefined);

  const triggerWarning = (rawMessage: any) => {
    const message = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;
    setWarningInfo({
      isOpen: true,
      raiIdx: message.raiIdx,
      shipName: message.shipName,
      name: message.name,
      phoneNumber: message.phoneNumber,
      lastReceivedTime: message.lastReceivedTime,
      lastLocLat: message.lastLocLat,
      lastLocLon: message.lastLocLon,
      alimType: message.type,
    });
  };

  const handleCloseModal = () => {
    setWarningInfo(undefined);
  };

  const handleRegister = () => {
    if (!warningInfo) return;
    setWarningInfo(undefined);
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().split(' ')[0];
    const formattedLastReceivedTime = warningInfo.lastReceivedTime
      ? dayjs(warningInfo.lastReceivedTime).format('YYYY-MM-DD HH:mm:ss')
      : '-';
    const description = [
      `선박명 : ${warningInfo.shipName}`,
      `선장이름(전화번호) : ${warningInfo.name}(${warningInfo.phoneNumber})`,
      `마지막 위치수신 시간 : ${formattedLastReceivedTime}`,
    ].join('\n');
    onRegisterCase(
      currentDate,
      currentTime,
      warningInfo.lastLocLat,
      warningInfo.lastLocLon,
      '확인중',
      description,
      undefined,
      '확인중',
      '확인중',
      '확인중',
      '실종자 수색',
      '해양경찰',
      warningInfo.phoneNumber,
      warningInfo.alimType
    );
    dispatch(
      ActionCtor.setPassenger([
        {
          phone: warningInfo.phoneNumber,
          name: warningInfo.name,
        },
      ])
    );
  };

  return (
    <CenterTemplateWrap>
      <CenterHeader
        title="스마트 수색구조시스템"
        userinfo={userInfo}
        onInitialize={onInitialize}
        onWarning={triggerWarning}
        isCenter={true}
      ></CenterHeader>
      <CenterWrap>
        <Vertical1stWrap>
          <Vertical4thWrap>
            <VideoWrap id="localDisplay" />
            <CenterRescue />
            <CenterVictim onClick={sendControl} />
          </Vertical4thWrap>
        </Vertical1stWrap>
        <Vertical2ndWrap>
          <Vertical5thtWrap>
            <CenterMap
              userinfo={userInfo}
              participants={participant}
              roomId={roomId}
              message={message}
              onRegisterCase={onRegisterCase}
              onInitialize={onInitialize}
              onDisconnect={disconnect}
              onMute={onMute}
            />
          </Vertical5thtWrap>
          <CenterMessage userinfo={userInfo} onClick={sendInviteMessage}></CenterMessage>
        </Vertical2ndWrap>
        <Vertical3rdWrap>
          <CenterStream onControl={onControl} refreshMediaTimestamp={refreshMediaTimestamp} />
          <CenterList onClick={selectRoom} onSetDate={onSetDate} onRegisterCase={onRegisterCase}></CenterList>
        </Vertical3rdWrap>
        {loading ? <Loading /> : null}
        <ModalAlert info={warningInfo} onClose={handleCloseModal} onRegister={handleRegister} />
      </CenterWrap>
    </CenterTemplateWrap>
  );
};

const CenterTemplateWrap = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
`;
const CenterWrap = styled.div`
  position: relative;
  top: 3.75rem;
  height: calc(100% - 3.75rem);
  min-height: 63.75rem;
  display: flex;
  overflow-y: hidden;
`;
const Vertical1stWrap = styled.div`
  min-width: 18.75rem;
  height: 100%;
`;
const Vertical2ndWrap = styled.div`
  width: 100%;
  min-width: 57.5rem;
  // min-height: 42.625rem;
  border: 1px solid #000000;
`;
const Vertical3rdWrap = styled.div`
  height: 100%;
  min-height: 63.75rem;
`;
const Vertical4thWrap = styled.div`
  height: 100%;
`;
const Vertical5thtWrap = styled.div`
  height: calc(100% - 11.125rem);
`;
const VideoWrap = styled.div`
  width: 1px;
  height: 0px;
`;
const ChatButton = styled.button`
  position: absolute;
  left: 15.88rem;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 2;
`;

export default CenterTemplate;
